import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import config from 'config'
import PageContainer from 'srcReact/PageContainer/PageContainer'
import styles from './styles.module.scss'
import { urlSearchParams } from 'srcReact/helpers/utils'
import Location from 'srcReact/GroupPage/CreateUpdateGroup/components/Location'
import DescriptionTextArea from 'srcReact/GroupPage/CreateUpdateGroup/components/DescriptionTextArea'
import WorkplaceSelection from './components/WorkplaceSelection'
import AdvanceSettings from 'srcReact/GroupPage/CreateUpdateGroup/components/AdvanceSettings'
import PrivacySetting from 'srcReact/GroupPage/CreateUpdateGroup/components/PrivacySetting'
import GroupName from 'srcReact/GroupPage/CreateUpdateGroup/components/GroupName'
import GroupType from 'srcReact/GroupPage/CreateUpdateGroup/components/GroupType'
import GroupPicture from './components/GroupPicture'
import GroupInterests from './components/GroupInterests'
import Loader from 'srcReact/components/common2/Loader'
import Popup from 'reactjs-popup'
import { alertActions, AlertContainer } from 'srcReact/Alert'
import Button from 'srcReact/components/common2/Button'
import ServerErrors from 'srcReact/components/popinsContent/ServerErrors'
import Modal from 'srcReact/components/common2/Modal'
import history from 'srcReact/helpers/history'
import { commonActions } from 'srcReact/components'
import colorVariables from 'srcReact/assets/scss/global/base/_variables.scss'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import { createUpdateGroupActions } from './actions'

const CreateGroup = (props) => {
  const { t } = useTranslation()
  const [noWorkplaceError, setNoWorkplaceError] = useState('')
  const [showLoader, setShowLoader] = useState(false)
  const lastContainerRef = useRef(null)

  const dispatch = useDispatch()
  const {
    login: { user: loggedInUser },
    organization: { current_organization },
    common: { selectedInterests, isNative },
    alert,
    createUpdateGroup: {
      formCompleted,
      groupCreation,
      workplaces,
      selectedWorkplace,
      groupType,
      groupName,
      skipPicture,
      description,
      location,
      privacySetting,
      createdGroupId,
      createdGroupName,
      createdGroupUrl,
      isGroupNameVisible,
      isGroupTypeVisible,
      isLocationVisible,
      isInterestsVisible,
      isPictureUploadVisible,
      isPrivacySettingsVisible,
      isDescriptionVisible,
      isAdvSettingsVisible,
      isGeneralGroup,
      loading,
      formValidationErrors,
      advanceSettings,
      groupPicture,
      createGroupErrors
    }
  } = useSelector((state) => state)

  const [showErrorPopup, setShowErrorPopup] = useState(false)

  useEffect(() => {
    if (isAdvSettingsVisible) {
      lastContainerRef.current.scrollIntoView(true, { behavior: 'smooth' })
    }
  }, [isAdvSettingsVisible])

  useEffect(() => {
    setNoWorkplaceError('')
    if (props.match.params.workplaceId) {
      dispatch(createUpdateGroupActions.setAsGeneralGroup())
    }

    // nativeapp
    if (isNative) {
      dispatch(createUpdateGroupActions.getWorkplacesForCreation(urlSearchParams('organization_id')))
      return
    }

    dispatch(createUpdateGroupActions.getWorkplacesForCreation(current_organization.info.resourceKey))
  }, [dispatch])

  useEffect(() => {
    return () => {
      dispatch(createUpdateGroupActions.clearData())
      dispatch(commonActions.updateSelectedInterests([]))
      dispatch(commonActions.fileCreationRemove())
    }
  }, [])

  useEffect(() => {
    if (createGroupErrors && createGroupErrors.length > 0) {
      setShowLoader(false)
      setShowErrorPopup(true)
    } else {
      setShowErrorPopup(false)
    }
  }, [createGroupErrors])
  useEffect(() => {
    setNoWorkplaceError('')
    if (workplaces) {
      if (isGeneralGroup) {
        const workplaceId = parseInt(props.match.params.workplaceId)
        let isValidWorkplaceId = false
        let selectedWorkplace = null

        if (workplaces.length > 0 && !isNaN(workplaceId)) {
          selectedWorkplace = workplaces.find((workplace) => workplace.id === workplaceId)
          isValidWorkplaceId = selectedWorkplace !== undefined
          // isValidWorkplaceId = workplaces[0].id === parseInt(workplaceId)
        }

        if (!isValidWorkplaceId) {
          setNoWorkplaceError(t('group.createGroup.noWorkplaceError'))
        } else {
          dispatch(createUpdateGroupActions.setSelectedWorkplace(selectedWorkplace))
          setNoWorkplaceError('')
        }
      } else {
        if (workplaces.length === 1) {
          dispatch(createUpdateGroupActions.setSelectedWorkplace(workplaces[0]))
        } else if (workplaces.length === 0) {
          setNoWorkplaceError(t('group.createGroup.noWorkplaceError'))
        } else {
          setNoWorkplaceError('')
        }
      }
    }
  }, [workplaces])

  useEffect(() => {
    if (createdGroupUrl.length > 0) {
      //nativeapp
      if (isNative) {
        window.location = config.webUrl + `/0/home/group/${createdGroupId}`
        return
      }
      history.push(`/0/home/group/${createdGroupId}`)
      //   window.location.href = createdGroupUrl
    }
  }, [createdGroupId, createdGroupName, createdGroupUrl])

  useEffect(() => {
    if (formCompleted) {
      let errors = []
      if (!isGeneralGroup) {
        if (!selectedWorkplace || !selectedWorkplace.id) {
          errors.push(t('group.createGroup.workplace.title2'))
        }
      }
      if (groupName && groupName.value.length === 0) {
        errors.push(t('error.required', { field: t(`formlabel.groupName`) }))
      }
      if (description && description.value.length === 0) {
        errors.push(t('error.required', { field: t(`formlabel.groupDescription`) }))
      }
      if (!isGeneralGroup) {
        if (
          !selectedInterests ||
          (selectedInterests && selectedInterests.length < appConstantSettings.MIN_INTEREST_COUNT) ||
          selectedInterests.length > appConstantSettings.MAX_INTEREST_COUNT
        ) {
          errors.push(
            t('group.createGroup.interest.minMaxInterestError', {
              min: appConstantSettings.MIN_INTEREST_COUNT,
              max: appConstantSettings.MAX_INTEREST_COUNT
            })
          )
        }
      }
      if (!isGeneralGroup) {
        if (!location || !location.value) {
          errors.push(t('error.required', { field: t(`formlabel.location`) }))
        }
      }
      if (!isGeneralGroup) {
        if (privacySetting.length === 0) {
          errors.push(t('error.choose', { field: t(`formlabel.privacySetting`) }))
        }
      }

      dispatch(createUpdateGroupActions.setValidationErrors(errors))
    }
  }, [formCompleted, selectedWorkplace, groupName, description, selectedInterests, privacySetting, location])

  const closeLoaderModal = () => {
    setShowLoader(false)
  }
  const closeErrorPopup = () => {
    setShowErrorPopup(false)
  }

  const createGroup = () => {
    setShowLoader(true)
    if (formValidationErrors && formValidationErrors.length > 0) {
      dispatch(alertActions.error(formValidationErrors))
      return
    }
    const memberCanSuggestMixr =
      advanceSettings.mixrSettings.userSelected > -1
        ? advanceSettings.mixrSettings.userSelected
        : advanceSettings.mixrSettings.default

    if (!isGeneralGroup) {
      const selectedInterstsIds = selectedInterests ? selectedInterests.map((i) => i.id).join(',') : ''

      const autoApprovalToJoin =
        advanceSettings.admissionSettings.userSelected > -1
          ? advanceSettings.admissionSettings.userSelected
          : advanceSettings.admissionSettings.default

      const ageRequirements =
        advanceSettings.memberSettings.userSelected !== null && advanceSettings.memberSettings.userSelected.age > 0
          ? advanceSettings.memberSettings.userSelected.age
          : advanceSettings.memberSettings.default.age

      const groupInfo = {
        name: groupName ? groupName.value : '',
        picture_id: groupPicture ? groupPicture.id : '',
        picture_name: groupPicture ? groupPicture.filename : '',
        picture_mime_type: groupPicture ? groupPicture.mimeType : '',
        description: description ? description.value : '',
        interest_ids: selectedInterstsIds,
        location: location && location.value,
        privacy_setting: privacySetting,
        age_requirements: ageRequirements,
        automatic_approval_to_join: autoApprovalToJoin,
        members_can_suggest_mixr: memberCanSuggestMixr,
        group_type: groupType
      }
      dispatch(createUpdateGroupActions.createGroup(selectedWorkplace.id, groupInfo))
    } else {
      const groupInfo = {
        name: groupName ? groupName.value : '',
        picture_id: groupPicture ? groupPicture.id : '',
        picture_name: groupPicture ? groupPicture.filename : '',
        picture_mime_type: groupPicture ? groupPicture.mimeType : '',
        description: description ? description.value : '',
        members_can_suggest_mixr: memberCanSuggestMixr,
        is_general: 1,
        group_type: 'interest'
      }
      dispatch(createUpdateGroupActions.createGroup(selectedWorkplace.id, groupInfo))
    }
  }
  return (
    <PageContainer pageClassName={styles.pageContainer} pageTitle={t('group.createGroup.title')} withFooter={false}>
      {alert && alert.message ? (
        <AlertContainer
          title={t('error.error_alert_title')}
          message={alert.message}
          show={!!alert.message}
          handleClose={() => dispatch(alertActions.clear())}
        />
      ) : null}
      <div className={styles.container}>
        {workplaces && workplaces.length > 0 && noWorkplaceError.length === 0 ? (
          <React.Fragment>
            {isGeneralGroup ? (
              <React.Fragment>
                <h1 className={styles.title}>{t('group.createGeneralGroup.title')}</h1>
                <p className={styles.para}>{t('group.createGeneralGroup.description')}</p>
              </React.Fragment>
            ) : (
              <h1 className={styles.title}>{t('group.createGroup.title')}</h1>
            )}

            {groupCreation.authorized === 1 && (
              <div className={styles.parentContainer}>
                <WorkplaceSelection
                  show={groupCreation.authorized === 1}
                  containerCss={styles.subContainer}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  buttonWrapperCss={styles.buttonWrapper}
                  verticalLineCss={styles.verticalLine}
                />
                <GroupType
                  nextSectionVisible={isGroupNameVisible}
                  show={isGroupTypeVisible && !isGeneralGroup}
                  hideNextButton={isGroupNameVisible}
                  containerCss={styles.subContainer}
                  innerContainerCss={styles.content}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  buttonWrapperCss={styles.buttonWrapper}
                  verticalLineCss={styles.verticalLine}
                  workplaces={workplaces}
                  selectedWorkplace={selectedWorkplace}
                  isGeneralGroup={isGeneralGroup}
                  loggedInUser={loggedInUser}
                />
                <GroupName
                  nextSectionVisible={isPictureUploadVisible}
                  show={isGroupNameVisible || isGeneralGroup}
                  hideNextButton={isPictureUploadVisible}
                  containerCss={styles.subContainer}
                  innerContainerCss={styles.content}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  buttonWrapperCss={styles.buttonWrapper}
                  verticalLineCss={styles.verticalLine}
                />

                <GroupPicture
                  nextSectionVisible={skipPicture === true || isDescriptionVisible}
                  show={isPictureUploadVisible}
                  hideNextButton={isDescriptionVisible}
                  containerCss={styles.subContainer}
                  innerContainerCss={styles.content}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  buttonWrapperCss={styles.buttonWrapper}
                  verticalLineCss={styles.verticalLine}
                />
                <DescriptionTextArea
                  nextSectionVisible={isGeneralGroup ? isAdvSettingsVisible : isInterestsVisible}
                  show={isDescriptionVisible}
                  hideNextButton={isInterestsVisible}
                  containerCss={styles.subContainer}
                  innerContainerCss={styles.content}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  buttonWrapperCss={styles.buttonWrapper}
                  verticalLineCss={styles.verticalLine}
                />
                <GroupInterests
                  nextSectionVisible={isLocationVisible}
                  show={isInterestsVisible && !isGeneralGroup}
                  hideNextButton={isLocationVisible}
                  containerCss={styles.subContainer}
                  innerContainerCss={styles.content}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  buttonWrapperCss={styles.buttonWrapper}
                  verticalLineCss={styles.verticalLine}
                />

                <Location
                  nextSectionVisible={isPrivacySettingsVisible}
                  show={isLocationVisible && !isGeneralGroup}
                  hideNextButton={isPrivacySettingsVisible}
                  containerCss={styles.subContainer}
                  innerContainerCss={styles.content}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  buttonWrapperCss={styles.buttonWrapper}
                  verticalLineCss={styles.verticalLine}
                />
                <PrivacySetting
                  nextSectionVisible={isAdvSettingsVisible}
                  show={isPrivacySettingsVisible && !isGeneralGroup}
                  containerCss={styles.subContainer}
                  innerContainerCss={styles.content}
                  infoCss={styles.para}
                  titleCss={styles.subTitle}
                  verticalLineCss={styles.verticalLine}
                />

                {/* <PrivacySetting nextSectionVisible={isAdvSettingsVisible} show={isPrivacySettingsVisible} /> */}
                <div
                  ref={lastContainerRef}
                  className={`${styles.subContainer}  ${styles.lastSubContainer}  ${
                    isAdvSettingsVisible && formValidationErrors.length == 0 ? styles.show : styles.hide
                  }`}>
                  <div class={`${styles.verticalLine} ${styles.lastVerticalLine}`} />
                  {formCompleted &&
                    formValidationErrors &&
                    formValidationErrors.length > 0 &&
                    alert &&
                    alert.message && (
                      <AlertContainer
                        title={t('group.createGroup.invalidForm')}
                        message={alert.message}
                        show={!!alert.message}
                        handleClose={() => dispatch(alertActions.clear())}
                      />
                    )}
                  <AdvanceSettings
                    show={isAdvSettingsVisible}
                    hasMemberSetting={isGeneralGroup ? false : true}
                    hasMixrSetting={true}
                    hasAdmission={isGeneralGroup ? false : true}
                    innerContainerCss={styles.content}
                    infoCss={styles.para}
                  />

                  {formCompleted && (
                    <div className={styles.summaryAndSubmit}>
                      <div className={styles.summaryContainer}>
                        <p className={styles.para}>{t('group.createGroup.summary1')}</p>
                        <p className={styles.para}>{t('group.createGroup.summary2')}</p>
                      </div>
                      <div className={`${styles.buttonWrapper}`}>
                        <Button
                          size='large'
                          text={
                            isGeneralGroup
                              ? t('group.createGeneralGroup.btnCreateGroup')
                              : t('group.createGroup.btnCreateGroup')
                          }
                          handleClick={createGroup}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        ) : noWorkplaceError.length > 0 && !loading ? (
          <p className={styles.para}>{t('group.createGroup.notAuthorizedError')}</p>
        ) : null}
      </div>
      {showLoader && (
        <Popup
          nested
          contentStyle={{ background: colorVariables.whiteBG, border: '0', width: '100vw', height: '100vh' }}
          overlayStyle={{ background: 'rgba(46, 45, 45, 0.7)', backdropFilter: 'blur(7px)', zIndex: 1040 }}
          open={showLoader}
          onClose={closeLoaderModal}
          closeOnDocumentClick={false}>
          <Loader
            title={
              // !loading && createdGroupUrl.length > 0 && createdGroupName.length > 0
              //   ? t('group.createGroup.btnRedirect', { group: createdGroupName }):
              isGeneralGroup
                ? t('group.createGeneralGroup.creatingGeneralGroup', { group: groupName.value })
                : t('group.createGroup.creatingGroup', { group: groupName.value })
            }
            closeModal={closeLoaderModal}
          />
        </Popup>
      )}
      {showErrorPopup && (
        <Modal isOpen={showErrorPopup} handleClose={closeErrorPopup}>
          <ServerErrors
            errors={createGroupErrors}
            title={t('group.createGroup.serverErrorTitle')}
            closeModal={closeErrorPopup}
          />
        </Modal>
      )}
    </PageContainer>
  )
}

export default CreateGroup
